import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { Html5Qrcode } from "html5-qrcode";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import { usePopup } from "../../utils/PopupContext";
import styles from "./LoadingEntry.module.css";
import FloatingActions from "../../components/FloatingActions";

const LoadingEntry = () => {
  const user = useSelector(selectUser);
  const token = user?.token;
  const { showPopup } = usePopup();

  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const qrRef = useRef(null);
  const scannerRef = useRef(null);
  const beforePhotoRef = useRef(null);
  const afterPhotoRef = useRef(null);
  const beforeInputRef = useRef(null);
  const afterInputRef = useRef(null);

  const [loadingPoints, setLoadingPoints] = useState([]);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [gps, setGps] = useState(null);

  const [qrText, setQrText] = useState("");
  const [scanning, setScanning] = useState(false);

  const [header, setHeader] = useState(null);
  const [items, setItems] = useState([]);

  const [beforePhoto, setBeforePhoto] = useState(null);
  const [afterPhoto, setAfterPhoto] = useState(null);
  const [beforePreview, setBeforePreview] = useState("");
  const [afterPreview, setAfterPreview] = useState("");

  const [remark, setRemark] = useState("");
  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [pointMismatch, setPointMismatch] = useState(false);
  const [transactionLocked, setTransactionLocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [itemQtys, setItemQtys] = useState({}); // { [slno]: "qty string" }

  const authHeader = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
    [token]
  );

  /*--
  const notify = (message, type = "info") => {
    const finalMessage =
      typeof message === "string" && message.trim()
        ? message.trim()
        : "Something went wrong.";

    try {
      if (typeof showPopup === "function") {
        // Most safe for your PopupContext-style issue
        showPopup(finalMessage, type);
        return;
      }
    } catch (e) {
      console.warn("showPopup failed:", e);
    }

    alert(finalMessage);
  };
  --*/

  const notify = (message, type = "info") => {
    const finalMessage =
      typeof message === "string" && message.trim()
        ? message.trim()
        : "Something went wrong.";

    // Map short type → popup variant + title (matches UnloadingEntry pattern)
    const variantMap = {
      success: { variant: "success", title: "Success" },
      error: { variant: "error", title: "Error" },
      warning: { variant: "warning", title: "Warning" },
      info: { variant: "info", title: "Notice" },
    };

    const cfg = variantMap[type] || variantMap.info;

    try {
      if (typeof showPopup === "function") {
        showPopup({
          variant: cfg.variant,
          title: cfg.title,
          message: finalMessage,
          autocloseMs: type === "success" ? 1500 : undefined,
        });
        return;
      }
    } catch (e) {
      console.warn("showPopup failed:", e);
    }

    alert(finalMessage);
  };

  useEffect(() => {
    fetchLoadingPoints();
    getGps();

    return () => {
      stopScanner();
      if (beforePreview) URL.revokeObjectURL(beforePreview);
      if (afterPreview) URL.revokeObjectURL(afterPreview);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToRef = (ref) => {
    setTimeout(() => {
      ref.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const resetTransactionState = () => {
    setQrText("");
    setHeader(null);
    setItems([]);
    setItemQtys({});
    setBeforePhoto(null);
    setAfterPhoto(null);

    if (beforePreview) URL.revokeObjectURL(beforePreview);
    if (afterPreview) URL.revokeObjectURL(afterPreview);

    setBeforePreview("");
    setAfterPreview("");
    setRemark("");
    setStarted(false);
    setCompleted(false);
    setPointMismatch(false);
    setTransactionLocked(false);

    if (beforeInputRef.current) beforeInputRef.current.value = "";
    if (afterInputRef.current) afterInputRef.current.value = "";

    scrollToRef(topRef);
  };

  const fetchLoadingPoints = async () => {
    try {
      const res = await axios.get("/api/loading/getMyLoadingPoints", authHeader);
      setLoadingPoints(res.data || []);
    } catch (err) {
      console.error(err);
      notify("Unable to load loading points.", "error");
    }
  };

  const getGps = () => {
    if (!navigator.geolocation) {
      notify("GPS is not supported on this device.", "error");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        });
      },
      () => {
        notify("Please allow GPS permission.", "error");
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  };

  const distanceMeter = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const toRad = (v) => (v * Math.PI) / 180;

    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const autoDetectPoint = () => {
    if (!gps) {
      notify("Please capture GPS first.", "error");
      return;
    }

    let nearest = null;

    loadingPoints.forEach((p) => {
      if (!p.latitude || !p.longitude) return;

      const dist = distanceMeter(
        gps.latitude,
        gps.longitude,
        Number(p.latitude),
        Number(p.longitude)
      );

      if (!nearest || dist < nearest.distance) {
        nearest = { ...p, distance: dist };
      }
    });

    if (!nearest) {
      notify("No loading point found near your GPS.", "error");
      return;
    }

    if (nearest.distance > Number(nearest.radius_meter || 100)) {
      notify("You are not inside any configured loading point area.", "error");
      return;
    }

    setSelectedPoint(nearest);
    notify(`Loading point selected: ${nearest.label}`, "success");
  };

  const parseQr = (text) => {
    const parts = String(text || "").trim().split("|");

    if (parts.length !== 3) {
      throw new Error("Invalid QR. Expected format: Entity|TCode|DocNumber");
    }

    const [p_entity_code, p_tcode, p_docnumber] = parts.map((x) => x.trim());

    if (!p_entity_code || !p_tcode || !p_docnumber) {
      throw new Error("QR data is incomplete.");
    }

    return { p_entity_code, p_tcode, p_docnumber };
  };

  const startScanner = async () => {
    try {
      if (!selectedPoint) {
        notify("Please select loading point first.", "error");
        return;
      }

      if (!window.isSecureContext) {
        notify(
          "Camera scanning is blocked on HTTP. Please open this screen using HTTPS domain.",
          "error"
        );
        return;
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        notify(
          "Camera is not available in this browser. Please use mobile Chrome with HTTPS.",
          "error"
        );
        return;
      }

      scrollToRef(qrRef);
      await stopScanner();

      const qrRegionId = "loading-qr-reader";
      scannerRef.current = new Html5Qrcode(qrRegionId);

      setScanning(true);

      await scannerRef.current.start(
        { facingMode: "environment" },
        {
          fps: 12,
          qrbox: (viewfinderWidth, viewfinderHeight) => {
            const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
            const qrboxSize = Math.floor(minEdge * 0.72);
            return {
              width: qrboxSize,
              height: qrboxSize,
            };
          },
          aspectRatio: 1.0,
        },
        async (decodedText) => {
          await stopScanner();
          setQrText(decodedText);
          await fetchAdvice(decodedText);
        }
      );
    } catch (err) {
      console.error(err);
      setScanning(false);

      const msg = String(err?.message || err || "");

      if (msg.includes("Camera streaming not supported")) {
        notify(
          "Camera scanning is not supported on this URL/browser. Please use HTTPS URL on mobile Chrome.",
          "error"
        );
        return;
      }

      notify(
        err?.message || "Unable to start QR scanner. Please check camera permission.",
        "error"
      );
    }
  };

  const stopScanner = async () => {
    try {
      if (!scannerRef.current) return;

      const state = scannerRef.current.getState?.();

      if (state === 2) {
        await scannerRef.current.stop();
      }

      await scannerRef.current.clear();
      scannerRef.current = null;
    } catch (err) {
      console.warn("Scanner cleanup ignored:", err?.message || err);
      scannerRef.current = null;
    } finally {
      setScanning(false);
    }
  };

  const fetchAdvice = async (text = qrText) => {
    try {
      if (!selectedPoint) {
        notify("Please select loading point first.", "error");
        return;
      }

      const payload = parseQr(text);
      setBusy(true);

      const res = await axios.post(
        "/api/loading/getAdviceDetails",
        payload,
        authHeader
      );

      const adviceHeader = res.data?.header;
      const adviceItems = res.data?.items || [];

      console.log("FULL RES.DATA:", JSON.stringify(res.data, null, 2));
      console.log("TOP-LEVEL KEYS:", Object.keys(res.data || {}));
      console.log("HEADER KEYS:", Object.keys(res.data?.header || {}));

      if (!adviceHeader) {
        notify("Loading advice not found.", "error");
        setHeader(null);
        setItems([]);
        setStarted(false);
        setCompleted(false);
        setPointMismatch(false);
        setTransactionLocked(false);
        return;
      }

      const isLocked =
        Boolean(adviceHeader.out_date) ||
        Boolean(adviceHeader.OUT_DATE) ||
        Boolean(adviceHeader.second_weight_completed) ||
        Boolean(adviceHeader.SECOND_WEIGHT_COMPLETED) ||
        Boolean(adviceHeader.p_secondwt) ||
        Boolean(adviceHeader.P_SECONDWT);

      const isMismatch =
        selectedPoint?.value &&
        adviceHeader?.loading_point_code &&
        String(selectedPoint.value).trim().toUpperCase() !==
        String(adviceHeader.loading_point_code).trim().toUpperCase();

      setHeader(adviceHeader);
      setItems(adviceItems);

      // Pre-fill qty inputs from existing qty_loaded values
      const initialQtys = {};
      adviceItems.forEach((it) => {
        if (it.qty_loaded !== null && it.qty_loaded !== undefined) {
          initialQtys[it.slno] = String(it.qty_loaded);
        }
      });
      setItemQtys(initialQtys);

      setStarted(Boolean(adviceHeader.p_loading_start));
      setCompleted(Boolean(adviceHeader.p_loading_end));
      setPointMismatch(Boolean(isMismatch));
      setTransactionLocked(Boolean(isLocked));
      setRemark(adviceHeader.P_LoadUnloadRemark || adviceHeader.p_load_unload_remark || "");

      if (isLocked) {
        notify("Transaction Completed, can't change.", "error");
      } else if (isMismatch) {
        notify(
          `Wrong loading point. Selected: ${selectedPoint?.label || selectedPoint?.value
          }, Advice: ${adviceHeader.loading_point_name || adviceHeader.loading_point_code
          }. Please contact Dispatch Team.`,
          "error"
        );
      } else if (adviceHeader.p_loading_start && !adviceHeader.p_loading_end) {
        notify("Loading already started. Please finish loading.", "info");
      } else if (adviceHeader.p_loading_start && adviceHeader.p_loading_end) {
        notify("Loading already completed. You may retake photos if required.", "info");
      }

      setTimeout(() => {
        document.getElementById("advice-details-card")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 200);
    } catch (err) {
      console.error(err);
      notify(
        err.response?.data?.message || err.message || "Unable to fetch advice.",
        "error"
      );
    } finally {
      setBusy(false);
    }
  };

  const chooseBeforePhoto = () => {
    if (transactionLocked) {
      notify("Transaction Completed, can't change.", "error");
      return;
    }
    scrollToRef(beforePhotoRef);
    beforeInputRef.current?.click();
  };

  const chooseAfterPhoto = () => {
    if (transactionLocked) {
      notify("Transaction Completed, can't change.", "error");
      return;
    }
    scrollToRef(afterPhotoRef);
    afterInputRef.current?.click();
  };

  const handleBeforePhotoChange = (e) => {
    const file = e.target.files?.[0] || null;
    setBeforePhoto(file);

    if (beforePreview) URL.revokeObjectURL(beforePreview);
    setBeforePreview(file ? URL.createObjectURL(file) : "");
  };

  const handleAfterPhotoChange = (e) => {
    const file = e.target.files?.[0] || null;
    setAfterPhoto(file);

    if (afterPreview) URL.revokeObjectURL(afterPreview);
    setAfterPreview(file ? URL.createObjectURL(file) : "");
  };

  const handleStartLoading = async () => {
    try {
      if (!header) {
        notify("Please scan Loading Advice first.", "error");
        return;
      }

      if (transactionLocked) {
        notify("Transaction Completed, can't change.", "error");
        return;
      }

      if (pointMismatch) {
        notify(
          "Cannot start loading. Selected loading point and advice loading point are different. Please contact Dispatch Team.",
          "error"
        );
        return;
      }

      if (!beforePhoto) {
        notify("Please take before loading photo.", "error");
        scrollToRef(beforePhotoRef);
        return;
      }

      setBusy(true);

      const formData = new FormData();
      formData.append("p_entity_code", header.p_entity_code);
      formData.append("p_tcode", header.p_tcode);
      formData.append("p_docnumber", header.p_docnumber);
      formData.append("photo_type", "before");
      formData.append("remark", remark || "");
      formData.append("photo", beforePhoto);

      const res = await axios.post("/api/loading/startLoading", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      notify(res.data?.message || "Data Saved.", "success");
      resetTransactionState();
    } catch (err) {
      console.error(err);
      notify(err.response?.data?.message || "Start loading failed.", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleFinishLoading = async () => {
    try {
      if (!header) {
        notify("Please scan Loading Advice first.", "error");
        return;
      }

      if (transactionLocked) {
        notify("Transaction Completed, can't change.", "error");
        return;
      }

      if (pointMismatch) {
        notify(
          "Cannot finish loading. Selected loading point and advice loading point are different. Please contact Dispatch Team.",
          "error"
        );
        return;
      }

      if (!started) {
        notify("Please start loading first.", "error");
        return;
      }

      if (!afterPhoto) {
        notify("Please take completion photo.", "error");
        scrollToRef(afterPhotoRef);
        return;
      }

      setBusy(true);

      const formData = new FormData();
      formData.append("p_entity_code", header.p_entity_code);
      formData.append("p_tcode", header.p_tcode);
      formData.append("p_docnumber", header.p_docnumber);
      formData.append("photo_type", "after");
      formData.append("remark", remark || "");
      formData.append("photo", afterPhoto);

      // NEW — pack item qtys as JSON array
      const qtyPayload = Object.entries(itemQtys)
        .filter(([_, v]) => v !== null && v !== undefined && String(v).trim() !== "")
        .map(([slno, qty]) => ({
          slno: Number(slno),
          qty_loaded: Number(qty),
        }))
        .filter((x) => Number.isFinite(x.slno) && Number.isFinite(x.qty_loaded));

      if (qtyPayload.length) {
        formData.append("item_qtys", JSON.stringify(qtyPayload));
      }

      const res = await axios.post("/api/loading/finishLoading", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      notify(res.data?.message || "Data Saved.", "success");
      resetTransactionState();
    } catch (err) {
      console.error(err);
      notify(err.response?.data?.message || "Finish loading failed.", "error");
    } finally {
      setBusy(false);
    }
  };

  const existingBeforeUrl = header?.p_before_url || "";
  const existingAfterUrl = header?.p_after_url || "";

  const existingStartTime =
    header?.p_loading_start || header?.P_LOADING_START || "";

  const existingEndTime =
    header?.p_loading_end || header?.P_LOADING_END || "";

  console.log("RENDER DEBUG:", {
    existingBeforeUrl,
    existingAfterUrl,
    beforePreview,
    afterPreview,
    header_p_before_url: header?.p_before_url,
    header_p_after_url: header?.p_after_url,
  });

  return (
    <div className={styles.page} ref={topRef}>
      <div className={styles.header}>
        <div>
          <div className={styles.title}>Loading Entry</div>
          <div className={styles.subTitle}>
            Scan advice, capture photos and complete loading
          </div>
        </div>

        <div
          className={
            completed
              ? styles.doneBadge
              : started
                ? styles.activeBadge
                : styles.pendingBadge
          }
        >
          {completed ? "Completed" : started ? "Started" : "Pending"}
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.sectionTitle}>1. Loading Point</div>

        <div className={styles.gpsRow}>
          <div className={styles.gpsText}>
            {gps
              ? `GPS: ${gps.latitude.toFixed(6)}, ${gps.longitude.toFixed(6)}`
              : "GPS not captured"}
          </div>

          <button className={styles.smallBtn} onClick={getGps} disabled={busy}>
            GPS
          </button>
        </div>

        <Select
          value={selectedPoint}
          options={loadingPoints}
          onChange={setSelectedPoint}
          placeholder="Select Loading Point"
          classNamePrefix="react-select"
        />

        <button
          className={styles.secondaryBtn}
          onClick={autoDetectPoint}
          disabled={busy}
        >
          Auto Detect by GPS
        </button>
      </div>

      <div className={styles.card}>
        <div className={styles.sectionTitle}>2. Scan Loading Advice QR</div>

        {!window.isSecureContext && (
          <div className={styles.warningBox}>
            Camera QR scanning needs HTTPS. Please open this screen using HTTPS
            domain for mobile camera scanning.
          </div>
        )}

        <div className={styles.qrShell}>
          <div id="loading-qr-reader" ref={qrRef} className={styles.qrBox}></div>
          {scanning && (
            <div className={styles.qrHint}>
              Align QR code inside the square box
            </div>
          )}
        </div>

        <div className={styles.btnRow}>
          {!scanning ? (
            <button
              className={styles.primaryBtn}
              onClick={startScanner}
              disabled={busy || !selectedPoint}
            >
              Start QR Scan
            </button>
          ) : (
            <button className={styles.dangerBtn} onClick={stopScanner}>
              Stop Scan
            </button>
          )}
        </div>

        <input
          className={styles.input}
          value={qrText}
          onChange={(e) => setQrText(e.target.value)}
          placeholder="Or paste QR text: Entity|TCode|DocNo"
        />

        <button
          className={styles.secondaryBtn}
          onClick={() => fetchAdvice()}
          disabled={busy || !selectedPoint}
        >
          Fetch Advice
        </button>
      </div>

      {header && (
        <div className={styles.card} id="advice-details-card">
          <div className={styles.sectionTitle}>3. Advice Details</div>

          {transactionLocked && (
            <div className={styles.warningBox}>
              Transaction Completed, can't change.
            </div>
          )}

          {pointMismatch && (
            <div className={styles.warningBox}>
              Wrong Loading Point. Selected point and advice point are different.
              Please contact Dispatch Team.
            </div>
          )}

          <div className={styles.infoGrid}>
            <div>
              <span>Doc No</span>
              <b>{header.p_docnumber}</b>
            </div>

            <div>
              <span>Vehicle</span>
              <b>{header.vehicle_no || "-"}</b>
            </div>

            <div>
              <span>Advice Point</span>
              <b>{header.loading_point_name || header.loading_point_code || "-"}</b>
            </div>

            <div>
              <span>TCode</span>
              <b>{header.p_tcode}</b>
            </div>

            <div>
              <span>Start Time</span>
              <b>{existingStartTime || "-"}</b>
            </div>

            <div>
              <span>End Time</span>
              <b>{existingEndTime || "-"}</b>
            </div>
          </div>

          <div className={styles.itemList}>
            {items.map((r) => (
              <div key={`item-${r.slno}`} className={styles.itemCard}>
                <div className={styles.itemHead}>
                  <div className={styles.itemCode}>{r.item_code}</div>
                  <div className={styles.itemReqQty}>
                    Required: <b>{r.qty_required ?? "-"}</b>
                  </div>
                </div>

                <div className={styles.itemDesc}>{r.item_desc}</div>

                <div className={styles.itemQtyRow}>
                  <label className={styles.itemQtyLabel}>Loaded Qty</label>
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.001"
                    min="0"
                    className={styles.itemQtyInput}
                    placeholder="Optional"
                    value={itemQtys[r.slno] ?? ""}
                    onChange={(e) =>
                      setItemQtys((prev) => ({
                        ...prev,
                        [r.slno]: e.target.value,
                      }))
                    }
                    disabled={transactionLocked}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {header && (
        <div className={styles.card}>
          <div className={styles.sectionTitle}>4. Loading Activity</div>

          <label className={styles.label}>Remark</label>
          <textarea
            className={styles.textarea}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder="Enter loading remark, if any"
            disabled={transactionLocked}
          />

          <div className={styles.photoBlock} ref={beforePhotoRef}>
            <div className={styles.photoTitle}>Before Loading Photo</div>

            {existingBeforeUrl && !beforePreview && (
              <img
                src={existingBeforeUrl}
                alt="Before Loading"
                className={styles.photoPreview}
              />
            )}

            {beforePreview && (
              <img
                src={beforePreview}
                alt="Before Loading Preview"
                className={styles.photoPreview}
              />
            )}

            {existingStartTime && (
              <div className={styles.timeText}>Started: {existingStartTime}</div>
            )}

            <input
              ref={beforeInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className={styles.hiddenFile}
              disabled={transactionLocked}
              onChange={handleBeforePhotoChange}
            />

            <button
              className={styles.photoBtn}
              type="button"
              onClick={chooseBeforePhoto}
              disabled={transactionLocked}
            >
              {started || existingBeforeUrl ? "Retake Start Photo" : "Take Photo"}
            </button>

            <button
              className={styles.primaryBtn}
              onClick={handleStartLoading}
              disabled={
                busy ||
                completed ||
                pointMismatch ||
                transactionLocked ||
                !beforePhoto
              }
            >
              {started ? "Update Start Photo" : "Start Loading"}
            </button>
          </div>

          <div className={styles.photoBlock} ref={afterPhotoRef}>
            <div className={styles.photoTitle}>After Loading Photo</div>

            {existingAfterUrl && !afterPreview && (
              <img
                src={existingAfterUrl}
                alt="After Loading"
                className={styles.photoPreview}
              />
            )}

            {afterPreview && (
              <img
                src={afterPreview}
                alt="After Loading Preview"
                className={styles.photoPreview}
              />
            )}

            {existingEndTime && (
              <div className={styles.timeText}>Finished: {existingEndTime}</div>
            )}

            <input
              ref={afterInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className={styles.hiddenFile}
              disabled={!started || transactionLocked}
              onChange={handleAfterPhotoChange}
            />

            <button
              className={styles.photoBtn}
              type="button"
              onClick={chooseAfterPhoto}
              disabled={!started || transactionLocked}
            >
              {completed || existingAfterUrl
                ? "Retake Finish Photo"
                : "Take Finish Photo"}
            </button>

            <button
              className={styles.successBtn}
              onClick={handleFinishLoading}
              disabled={
                busy ||
                !started ||
                pointMismatch ||
                transactionLocked ||
                !afterPhoto
              }
            >
              {completed ? "Update Finish Photo" : "Finish Loading"}
            </button>
          </div>
        </div>
      )}

      <FloatingActions topRef={topRef} bottomRef={bottomRef} />
      <div ref={bottomRef} />
    </div>
  );
};

export default LoadingEntry;